import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";

import {
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  SUPPORT_TICKET_TYPES,
  SupportTagSchema,
  type SupportTicketDetail,
  type SupportTicketUpdate,
} from "../../shared/support-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { updateSupportTicket } from "./support-api";
import { LabeledSelect } from "./support-fields";
import {
  formatWhen,
  PRIORITY_LABELS,
  replyDue,
  STATUS_LABELS,
  TYPE_LABELS,
} from "./support-format";

type Assignee = { employeeId: string; displayName: string };

const options = <T extends string>(values: readonly T[], labels: Record<T, string>) =>
  values.map((value) => ({ value, label: labels[value] }));

/** Everyone who may be assigned, plus the current assignee even if they no longer may. */
function assigneeOptions(ticket: SupportTicketDetail, assignees: readonly Assignee[]) {
  const current = ticket.assignee;
  const people =
    current === null || assignees.some((person) => person.employeeId === current.employeeId)
      ? assignees
      : [...assignees, current];
  return [
    { value: "", label: "Unassigned" },
    ...people.map((person) => ({ value: person.employeeId, label: person.displayName })),
  ];
}

/** The ticket's fields. Each change saves at once and appears in the thread as an event. */
export function SupportTicketFields({
  ticket,
  assignees,
  canWrite,
  onUpdated,
}: {
  ticket: SupportTicketDetail;
  assignees: readonly Assignee[];
  canWrite: boolean;
  onUpdated: (ticket: SupportTicketDetail) => void;
}) {
  const save = useMutation({
    mutationFn: (update: SupportTicketUpdate) => updateSupportTicket(ticket.id, update),
    onSuccess: (result) => {
      onUpdated(result.ticket);
    },
  });
  const disabled = !canWrite || save.isPending;
  return (
    <aside
      aria-label="Ticket fields"
      className="flex flex-col gap-4 rounded-xl bg-card px-5 py-5 shadow-card"
    >
      <LabeledSelect
        id="support-status"
        label="Status"
        value={ticket.status}
        disabled={disabled}
        options={options(SUPPORT_TICKET_STATUSES, STATUS_LABELS)}
        onChange={(value) => {
          save.mutate({ status: value as SupportTicketDetail["status"] });
        }}
      />
      <LabeledSelect
        id="support-assignee"
        label="Assignee"
        value={ticket.assignee?.employeeId ?? ""}
        disabled={disabled}
        options={assigneeOptions(ticket, assignees)}
        onChange={(value) => {
          save.mutate({ assigneeEmployeeId: value === "" ? null : value });
        }}
      />
      <LabeledSelect
        id="support-priority"
        label="Priority"
        value={ticket.priority}
        disabled={disabled}
        options={options(SUPPORT_TICKET_PRIORITIES, PRIORITY_LABELS)}
        onChange={(value) => {
          save.mutate({ priority: value as SupportTicketDetail["priority"] });
        }}
      />
      <LabeledSelect
        id="support-type"
        label="Type"
        value={ticket.type ?? ""}
        disabled={disabled}
        options={[{ value: "", label: "Not set" }, ...options(SUPPORT_TICKET_TYPES, TYPE_LABELS)]}
        onChange={(value) => {
          save.mutate({ type: value === "" ? null : (value as SupportTicketDetail["type"]) });
        }}
      />
      <TicketTags
        tags={ticket.tags}
        disabled={disabled}
        onChange={(update) => {
          save.mutate(update);
        }}
      />
      {save.error ? <FieldError>{save.error.message}</FieldError> : null}
      <FirstReply ticket={ticket} />
    </aside>
  );
}

function TicketTags({
  tags,
  disabled,
  onChange,
}: {
  tags: readonly string[];
  disabled: boolean;
  onChange: (update: SupportTicketUpdate) => void;
}) {
  const [draft, setDraft] = useState("");
  const parsed = SupportTagSchema.safeParse(draft);
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-semibold text-muted-foreground">Tags</span>
      <ul aria-label="Tags" className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <li key={tag} className="inline-flex items-center gap-1 rounded-full bg-muted pr-1 pl-3">
            <span className="text-sm">{tag}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`Remove tag ${tag}`}
              disabled={disabled}
              onClick={() => {
                onChange({ removeTags: [tag] });
              }}
            >
              <X />
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!parsed.success) return;
          onChange({ addTags: [parsed.data] });
          setDraft("");
        }}
      >
        <label htmlFor="support-tag" className="sr-only">
          Add a tag
        </label>
        <Input
          id="support-tag"
          value={draft}
          disabled={disabled}
          placeholder="Add a tag"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <Button type="submit" variant="outline" disabled={disabled || !parsed.success}>
          Add
        </Button>
      </form>
    </div>
  );
}

function FirstReply({ ticket }: { ticket: SupportTicketDetail }) {
  const due = replyDue(ticket.replyDueAt);
  return (
    <div className="flex flex-col gap-1 border-t border-muted pt-3 text-sm text-muted-foreground">
      {due ? (
        <p
          className={
            due.overdue ? "font-semibold text-destructive" : "font-semibold text-foreground"
          }
        >
          {due.text}
        </p>
      ) : null}
      <p>
        Opened {formatWhen(ticket.createdAt)}.{" "}
        {ticket.firstRepliedAt === null
          ? "No reply yet."
          : `First reply ${formatWhen(ticket.firstRepliedAt)}.`}
      </p>
    </div>
  );
}
